import { useCallback } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import { type useSetUserIsMetricSystemMutation_user$key } from '@/__generated__/useSetUserIsMetricSystemMutation_user.graphql'
import {
  type useSetUserIsMetricSystemMutation as UseSetUserIsMetricSystemMutation,
  type useSetUserIsMetricSystemMutation$data
} from '@/__generated__/useSetUserIsMetricSystemMutation.graphql'

type CommitAsyncFn = (enabled: boolean) => Promise<useSetUserIsMetricSystemMutation$data>

export const useSetUserIsMetricSystemMutation = (
  userRef: useSetUserIsMetricSystemMutation_user$key | null | undefined
): [CommitAsyncFn, boolean] => {
  const userData = useFragment(
    graphql`
      fragment useSetUserIsMetricSystemMutation_user on User {
        rowId
      }
    `,
    userRef
  )
  const [commit, isPending] = useMutation<UseSetUserIsMetricSystemMutation>(graphql`
    mutation useSetUserIsMetricSystemMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          isMetricSystem
          updatedAt
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (enabled: boolean) =>
      new Promise<useSetUserIsMetricSystemMutation$data>((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              resolve(response)
            }
          },
          // transport-level failures (offline, 5xx, WS drop) invoke onError, not onCompleted — without
          // this the promise would never settle and the caller would hang forever.
          onError: (error) => reject(error),
          variables: {
            input: {
              rowId: userData?.rowId,
              patch: {
                isMetricSystem: enabled
              }
            }
          }
        })
      ),
    [commit, userData?.rowId]
  )

  return [commitAsync, isPending]
}
